import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledSelect } from '@resistapp/client/components/forms/form-inputs/controlled-select';
import { theme } from '@resistapp/client/components/shared/theme';
import { MaybeApiError } from '@resistapp/client/components/states/error';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import {
  friendlySampleSubType,
  friendlySampleType,
  getParentType,
  SampleSubType,
  SampleType,
} from '@resistapp/common/sampling-point-types';
import { AccessLevel, PatchSamplingPointRequest, zPatchSamplingPointRequest } from '@resistapp/common/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  useAdminSamplingPointAccess,
  useCountries,
  useDeleteSamplingPointProcessFlow,
  usePatchSamplingPoint,
  usePostSamplingPointProcessFlow,
  usePostSamplingPointReverseProcessFlow,
  useSamplingPoint,
} from '../../hooks/api';

export function AdminSamplingPointView() {
  const params = useParams();
  const idParam = params.id;
  const samplingPointId = idParam && idParam !== 'new' ? Number(idParam) : undefined;
  const isEdit = typeof samplingPointId === 'number' && !Number.isNaN(samplingPointId);

  const navigate = useNavigateWithQuery();
  const { data: countriesByAlpha3 } = useCountries();
  const { data: samplingPoint, isPending, error } = useSamplingPoint(samplingPointId);
  const patchSmplPnt = usePatchSamplingPoint();
  const postProcessFlow = usePostSamplingPointProcessFlow();
  const deleteProcessFlow = useDeleteSamplingPointProcessFlow();
  const reverseProcessFlow = usePostSamplingPointReverseProcessFlow();
  const {
    data: samplingPointAccess,
    isPending: isSamplingPointAccessPending,
    error: samplingPointAccessError,
  } = useAdminSamplingPointAccess(samplingPointId);
  const queryClient = useQueryClient();
  const toast = useToast();

  const organisationsWithAccess = samplingPointAccess?.organisations ?? [];

  const defaultValues: Partial<PatchSamplingPointRequest> = {
    name: samplingPoint?.name || '',
    type: samplingPoint?.type,
    subtype: samplingPoint?.subtype || null,
    country: samplingPoint?.country || null,
    region: samplingPoint?.region || null,
    city: samplingPoint?.city || null,
    lat: samplingPoint?.lat || null,
    lon: samplingPoint?.lon || null,
    inferredLat: samplingPoint?.inferredLat || null,
    inferredLon: samplingPoint?.inferredLon || null,
    adminLevels: samplingPoint?.adminLevels || null,
  };

  const {
    handleSubmit,
    register,
    control,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<PatchSamplingPointRequest>({
    resolver: zodResolver(zPatchSamplingPointRequest),
    defaultValues,
  });

  // Reset form when sampling point data loads
  if (samplingPoint && !watch('name')) {
    reset(defaultValues);
  }

  // Only block the form on edit while the sampling point is loading; for create flow, do not block.
  const loading = (isEdit && isPending) || patchSmplPnt.isPending || isSubmitting;
  const apiError = error || patchSmplPnt.error || samplingPointAccessError;

  const watchedType = watch('type');
  const watchedLat = watch('lat');
  const watchedLon = watch('lon');

  const hasCoordinateMismatch = (watchedLat !== null) !== (watchedLon !== null);

  // Track previous type and store subtype values for each type
  const previousTypeRef = useRef(watchedType);
  const subtypeByTypeRef = useRef<Record<string, SampleSubType | null>>({});

  // Store/restore subtype when type changes
  useEffect(() => {
    const currentSubtype = watch('subtype') || null;

    // If this is a user change (not initial load)
    if (previousTypeRef.current && previousTypeRef.current !== watchedType) {
      // Store the current subtype for the previous type
      subtypeByTypeRef.current[previousTypeRef.current] = currentSubtype;

      // Restore subtype for the new type if we have one stored, otherwise clear
      const storedSubtype = subtypeByTypeRef.current[watchedType || ''];
      setValue('subtype', storedSubtype ?? null);
    }

    previousTypeRef.current = watchedType;
  }, [watchedType, setValue, watch]);

  const onSubmit = async (values: PatchSamplingPointRequest) => {
    try {
      if (isEdit && samplingPointId) {
        await patchSmplPnt.mutateAsync({
          ...values,
          id: samplingPointId,
        });
        navigate('/admin/sampling-points', false);
      } else {
        const created = await patchSmplPnt.mutateAsync({
          ...values,
          type: values.type as SampleType,
          inferredLat: null,
          inferredLon: null,
          adminLevels: null,
        });
        navigate(`/admin/sampling-point/${created.id}`, false);
      }
    } catch (submitError) {
      console.error('Form submission error:', submitError);
    }
  };

  const onValidationFailed = (validationErrors: any) => {
    console.warn('Form validation failed', validationErrors);
  };

  const countryOptions = useMemo(() => {
    if (!countriesByAlpha3) return [];
    return Object.entries(countriesByAlpha3).map(([alpha3]) => alpha3);
  }, [countriesByAlpha3]);

  const typeOptions = useMemo(() => Object.values(SampleType), []);

  const subtypeOptions = useMemo(() => {
    if (!watchedType) return [];
    return Object.values(SampleSubType).filter(st => getParentType(st) === watchedType);
  }, [watchedType]);

  const [flowDirection, setFlowDirection] = useState<'outgoing' | 'incoming'>('outgoing');
  const [otherFlowSamplingPointId, setOtherFlowSamplingPointId] = useState('');

  const refreshSamplingPointStable = useCallback(async () => {
    if (!samplingPointId) {
      return;
    }
    await queryClient.invalidateQueries({ queryKey: [`/sampling-point/${samplingPointId}`] });
  }, [queryClient, samplingPointId]);

  const addFlowStable = useCallback(async () => {
    if (!samplingPointId) {
      return;
    }

    const otherId = Number(otherFlowSamplingPointId);
    if (!Number.isFinite(otherId) || otherId <= 0) {
      toast({
        title: 'Invalid sampling point id',
        description: 'Enter a positive numeric sampling point id to link.',
        status: 'error',
        isClosable: true,
      });
      return;
    }

    const fromPointId = flowDirection === 'outgoing' ? samplingPointId : otherId;
    const toPointId = flowDirection === 'outgoing' ? otherId : samplingPointId;

    try {
      await postProcessFlow.mutateAsync({
        args: { samplingPointId },
        body: { fromPointId, toPointId },
      });
      await refreshSamplingPointStable();
      setOtherFlowSamplingPointId('');
      toast({
        title: 'Flow added',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (caughtError) {
      toast({
        title: 'Failed to add flow',
        description: caughtError instanceof Error ? caughtError.message : 'An error occurred',
        status: 'error',
        isClosable: true,
      });
    }
  }, [flowDirection, otherFlowSamplingPointId, postProcessFlow, refreshSamplingPointStable, samplingPointId, toast]);

  const deleteFlowStable = useCallback(
    async (processFlowId: number) => {
      if (!samplingPointId) {
        return;
      }
      if (!window.confirm(`Delete flow #${processFlowId}?`)) {
        return;
      }

      try {
        await deleteProcessFlow.mutateAsync({ samplingPointId, processFlowId });
        await refreshSamplingPointStable();
        toast({
          title: 'Flow deleted',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
      } catch (caughtError) {
        toast({
          title: 'Failed to delete flow',
          description: caughtError instanceof Error ? caughtError.message : 'An error occurred',
          status: 'error',
          isClosable: true,
        });
      }
    },
    [deleteProcessFlow, refreshSamplingPointStable, samplingPointId, toast],
  );

  const reverseFlowStable = useCallback(
    async (args: { processFlowId: number; fromLabel: string; toLabel: string }) => {
      if (!samplingPointId) {
        return;
      }
      const { processFlowId, fromLabel, toLabel } = args;
      if (
        !window.confirm(
          `Reverse flow #${processFlowId}?\n\n${fromLabel} -> ${toLabel}\nbecomes\n${toLabel} -> ${fromLabel}`,
        )
      ) {
        return;
      }

      try {
        await reverseProcessFlow.mutateAsync({ samplingPointId, processFlowId });
        await refreshSamplingPointStable();
        toast({
          title: 'Flow reversed',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
      } catch (caughtError) {
        toast({
          title: 'Failed to reverse flow',
          description: caughtError instanceof Error ? caughtError.message : 'An error occurred',
          status: 'error',
          isClosable: true,
        });
      }
    },
    [refreshSamplingPointStable, reverseProcessFlow, samplingPointId, toast],
  );

  if (isEdit && isPending) {
    return <LoadingView />;
  }

  return (
    <div style={{ padding: theme.spacing[4], marginBottom: theme.spacing[16] }}>
      <Flex justify="space-between" align="center" mb={theme.spacing[4]}>
        <Heading size="md">{isEdit ? `Sampling point #${samplingPointId}` : 'Create a sampling point'}</Heading>
      </Flex>
      <form onSubmit={e => void handleSubmit(onSubmit, onValidationFailed)(e)}>
        <Stack spacing={4} maxW={720} bg="white" p={theme.spacing[4]} borderRadius={6} boxShadow="sm">
          <FormControl isRequired isInvalid={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input {...register('name')} isDisabled={loading} />
          </FormControl>
          <Flex gap={4}>
            <Box flex="0 0 32%">
              <ControlledSelect
                isRequired
                control={control}
                name="type"
                options={typeOptions}
                placeholder="Select type"
                labeller={t => friendlySampleType(t)}
              />
            </Box>
            <Box flex="1">
              <ControlledSelect
                control={control}
                name="subtype"
                options={subtypeOptions}
                placeholder="Select subtype"
                labeller={st => friendlySampleSubType(st, watchedType)}
              />
            </Box>
          </Flex>
          <Flex gap={4}>
            <Box flex="1">
              <FormControl isInvalid={!!errors.city}>
                <FormLabel>City</FormLabel>
                <Input {...register('city')} isDisabled={loading} />
              </FormControl>
            </Box>
            <Box flex="1">
              <FormControl isInvalid={!!errors.region}>
                <FormLabel>Region</FormLabel>
                <Input {...register('region')} isDisabled={loading} />
              </FormControl>
            </Box>
            <Box flex="1">
              <ControlledSelect
                isRequired
                control={control}
                name="country"
                options={countryOptions}
                placeholder="Select country"
                labeller={alpha3 => countriesByAlpha3?.[alpha3]?.name || alpha3}
              />
            </Box>
          </Flex>
          <Flex gap={4}>
            <FormControl isInvalid={!!errors.lat}>
              <FormLabel>Latitude</FormLabel>
              <Input type="number" step="any" {...register('lat', { valueAsNumber: true })} isDisabled={loading} />
            </FormControl>
            <FormControl isInvalid={!!errors.lon}>
              <FormLabel>Longitude</FormLabel>
              <Input type="number" step="any" {...register('lon', { valueAsNumber: true })} isDisabled={loading} />
            </FormControl>
          </Flex>
          <Button
            type="submit"
            isLoading={loading}
            isDisabled={Object.keys(errors).length > 0 || hasCoordinateMismatch}
            colorScheme="blue"
          >
            {isEdit ? 'Save changes' : 'Create'}
          </Button>
        </Stack>
      </form>
      <MaybeApiError error={apiError} />

      {isEdit && samplingPoint?.relatedSamplingPoints ? (
        samplingPoint.relatedSamplingPoints.length > 0 ? (
          <Box mt={theme.spacing[6]}>
            <Heading size="md" mb={2} mt={2}>
              Related sampling points
            </Heading>
            <ul style={{ listStyleType: 'disc', paddingLeft: theme.spacing[4] }}>
              {samplingPoint.relatedSamplingPoints.map(le => (
                <li key={le.id} style={{ marginBottom: theme.spacing[1] }}>
                  <RouterLink to={`/admin/sampling-point/${le.id}`} style={{ fontWeight: 600 }}>
                    {le.name} ({le.id})
                  </RouterLink>
                  <Text
                    as="span"
                    color="gray.600"
                  >{` · ${le.subtype ? friendlySampleSubType(le.subtype, le.type) : friendlySampleType(le.type)}`}</Text>
                </li>
              ))}
            </ul>
          </Box>
        ) : (
          <Text mt={theme.spacing[6]} color="gray.600">
            No related sampling points.
          </Text>
        )
      ) : null}

      {isEdit && samplingPoint?.duplicateSamplingPoints ? (
        samplingPoint.duplicateSamplingPoints.length > 0 ? (
          <Box mt={theme.spacing[6]}>
            <Heading size="md" mb={2} mt={2}>
              Suspected duplicates
            </Heading>
            <ul style={{ listStyleType: 'disc', paddingLeft: theme.spacing[4] }}>
              {samplingPoint.duplicateSamplingPoints.map(de => (
                <li key={de.id} style={{ marginBottom: theme.spacing[1] }}>
                  <RouterLink to={`/admin/sampling-point/${de.id}`} style={{ fontWeight: 600 }}>
                    {de.name} ({de.id})
                  </RouterLink>
                </li>
              ))}
            </ul>
          </Box>
        ) : (
          <Text mt={theme.spacing[6]} color="gray.600">
            No duplicates.
          </Text>
        )
      ) : null}

      {isEdit && samplingPoint?.processes ? (
        <Box mt={theme.spacing[6]}>
          <Heading size="md" mb={2} mt={2}>
            Process memberships
          </Heading>

          {samplingPoint.processes.length > 0 ? (
            samplingPoint.processes.map(processInfo => {
              const samplingPointById = new Map(processInfo.samplingPoints.map(point => [point.id, point] as const));
              const processFlows = processInfo.process.flows ?? [];
              const sortedProcessFlows = processFlows.slice().sort((left, right) => {
                const leftFrom = samplingPointById.get(left.fromPointId)?.name ?? '';
                const rightFrom = samplingPointById.get(right.fromPointId)?.name ?? '';
                const fromCompare = leftFrom.localeCompare(rightFrom, undefined, { sensitivity: 'base' });
                if (fromCompare !== 0) {
                  return fromCompare;
                }

                const leftTo = samplingPointById.get(left.toPointId)?.name ?? '';
                const rightTo = samplingPointById.get(right.toPointId)?.name ?? '';
                const toCompare = leftTo.localeCompare(rightTo, undefined, { sensitivity: 'base' });
                if (toCompare !== 0) {
                  return toCompare;
                }

                return left.id - right.id;
              });

              return (
                <Box key={processInfo.process.id} mb={theme.spacing[3]}>
                  <Text fontWeight={600}>
                    {processInfo.process.name} ({processInfo.process.kind}) · #{processInfo.process.id}
                  </Text>
                  {sortedProcessFlows.length > 0 ? (
                    <Stack mt={2} spacing={1}>
                      {sortedProcessFlows.map(flow => {
                        const fromPoint = samplingPointById.get(flow.fromPointId);
                        const toPoint = samplingPointById.get(flow.toPointId);
                        const participates = flow.fromPointId === samplingPointId || flow.toPointId === samplingPointId;
                        const isCurrentFrom = flow.fromPointId === samplingPointId;
                        const isCurrentTo = flow.toPointId === samplingPointId;
                        const baseFromLabel = fromPoint
                          ? `${fromPoint.name} (${fromPoint.id})`
                          : `#${flow.fromPointId}`;
                        const baseToLabel = toPoint ? `${toPoint.name} (${toPoint.id})` : `#${flow.toPointId}`;
                        const fromLabel = baseFromLabel;
                        const toLabel = baseToLabel;
                        return (
                          <Flex key={flow.id} align="center" gap={2} wrap="wrap">
                            <RouterLink
                              to={`/admin/sampling-point/${flow.fromPointId}`}
                              style={{
                                fontWeight: isCurrentFrom ? 700 : 600,
                                backgroundColor: isCurrentFrom ? '#D0D8FF' : undefined,
                                borderRadius: isCurrentFrom ? 4 : undefined,
                                padding: isCurrentFrom ? '2px 6px' : undefined,
                              }}
                            >
                              {fromLabel}
                            </RouterLink>
                            <Text as="span" color="gray.600">
                              -&gt;
                            </Text>
                            <RouterLink
                              to={`/admin/sampling-point/${flow.toPointId}`}
                              style={{
                                fontWeight: isCurrentTo ? 700 : 600,
                                backgroundColor: isCurrentTo ? '#D0D8FF' : undefined,
                                borderRadius: isCurrentTo ? 4 : undefined,
                                padding: isCurrentTo ? '2px 6px' : undefined,
                              }}
                            >
                              {toLabel}
                            </RouterLink>
                            <Text as="span" color="gray.500">
                              #{flow.id}
                            </Text>
                            {participates ? (
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="orange"
                                isLoading={reverseProcessFlow.isPending}
                                onClick={() => {
                                  void reverseFlowStable({
                                    processFlowId: flow.id,
                                    fromLabel: baseFromLabel,
                                    toLabel: baseToLabel,
                                  });
                                }}
                              >
                                Reverse
                              </Button>
                            ) : null}
                            {participates ? (
                              <Button
                                size="xs"
                                variant="outline"
                                colorScheme="red"
                                isLoading={deleteProcessFlow.isPending}
                                onClick={() => {
                                  void deleteFlowStable(flow.id);
                                }}
                              >
                                Delete
                              </Button>
                            ) : null}
                          </Flex>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Text color="gray.600">No flows in this process.</Text>
                  )}
                </Box>
              );
            })
          ) : (
            <Text color="gray.600">No process linked to this sampling point.</Text>
          )}

          <Box mt={theme.spacing[4]}>
            <Heading size="sm" mb={2}>
              Add flow
            </Heading>
            <Flex gap={2} wrap="wrap">
              <Select
                value={flowDirection}
                onChange={e => {
                  setFlowDirection(e.target.value === 'incoming' ? 'incoming' : 'outgoing');
                }}
                width="220px"
                bg="white"
              >
                <option value="outgoing">This -&gt; other</option>
                <option value="incoming">Other -&gt; this</option>
              </Select>
              <Input
                width="220px"
                placeholder="Other sampling point id"
                value={otherFlowSamplingPointId}
                onChange={e => {
                  setOtherFlowSamplingPointId(e.target.value);
                }}
              />
              <Button
                onClick={() => {
                  void addFlowStable();
                }}
                isLoading={postProcessFlow.isPending}
                isDisabled={!otherFlowSamplingPointId.trim()}
                colorScheme="blue"
              >
                Add
              </Button>
            </Flex>
            <Text mt={2} fontSize="sm" color="gray.600">
              Only flows where this sampling point participates can be edited here.
            </Text>
          </Box>
        </Box>
      ) : null}

      {isEdit ? (
        <Box mt={theme.spacing[6]}>
          <Heading size="md" mb={2} mt={2}>
            Organisations with access
          </Heading>
          {isSamplingPointAccessPending ? (
            <Text color="gray.600">Loading organisations...</Text>
          ) : organisationsWithAccess.length > 0 ? (
            <ul style={{ listStyleType: 'disc', paddingLeft: theme.spacing[4] }}>
              {organisationsWithAccess.map(org => {
                const ownershipTag = org.accessLevel === AccessLevel.OWNER ? '(O)' : '(R)';
                return (
                  <li key={org.id} style={{ marginBottom: theme.spacing[1] }}>
                    <RouterLink to={`/admin/organisation/${org.id}`} style={{ fontWeight: 600 }}>
                      {org.name} ({org.id})
                    </RouterLink>
                    <Text as="span" color="gray.600" ml={2}>
                      {ownershipTag}
                    </Text>
                  </li>
                );
              })}
            </ul>
          ) : (
            <Text color="gray.600">No organisations have access.</Text>
          )}
        </Box>
      ) : null}

      {isEdit && samplingPoint?.sampleSheets ? (
        samplingPoint.sampleSheets.length > 0 ? (
          <Box mt={theme.spacing[6]}>
            <Heading size="md" mb={2} mt={2}>
              Sample sheets
            </Heading>
            <ul style={{ listStyleType: 'disc', paddingLeft: theme.spacing[4] }}>
              {samplingPoint.sampleSheets.map(sheet => (
                <li key={sheet.id} style={{ marginBottom: theme.spacing[1] }}>
                  {sheet.labWorkbookId ? (
                    <RouterLink to={`/lab/${sheet.labWorkbookId}`} style={{ fontWeight: 600 }}>
                      {sheet.name ?? sheet.id}
                    </RouterLink>
                  ) : (
                    <Text as="span" fontWeight={600}>
                      {sheet.name ?? sheet.id}
                    </Text>
                  )}
                  <Text
                    as="span"
                    color="gray.600"
                  >{` · ${sheet.sampleCount} sample${sheet.sampleCount === 1 ? '' : 's'}`}</Text>
                  {sheet.labSheetUrl ? (
                    <Text as="div" color="gray.600">
                      Legacy sheet:{' '}
                      <a href={sheet.labSheetUrl} target="_blank" rel="noreferrer">
                        {sheet.labSheetUrl}
                      </a>
                    </Text>
                  ) : null}
                </li>
              ))}
            </ul>
          </Box>
        ) : (
          <Text mt={theme.spacing[6]} color="gray.600">
            No sample sheets linked to this sampling point.
          </Text>
        )
      ) : null}

      {isEdit && samplingPoint?.projectSamples && samplingPoint.projectSamples.length > 0 ? (
        <Box mt={theme.spacing[6]}>
          <Heading size="md" mb={2} mt={2}>
            Linked projects and samples
          </Heading>
          <ul style={{ listStyleType: 'disc', paddingLeft: theme.spacing[4] }}>
            {samplingPoint.projectSamples.map(p => (
              <li key={p.id} style={{ marginBottom: theme.spacing[2] }}>
                <RouterLink to={`/research/${p.id}?samplingPoints=${samplingPointId}`} style={{ fontWeight: 600 }}>
                  {p.name} ({p.id})
                </RouterLink>
                {p.samples.length > 0 && (
                  <ul style={{ listStyleType: 'circle', paddingLeft: theme.spacing[4], marginTop: 4 }}>
                    {p.samples.map(s => (
                      <li key={s.id}>
                        <RouterLink to={`/admin/sample/${s.id}`}>
                          {s.number != null ? `${s.number}` : ''}
                          {s.bioRep ? s.bioRep : ''} ({s.id})
                        </RouterLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </Box>
      ) : (
        isEdit && (
          <Text mt={theme.spacing[6]} color="gray.600">
            No linked projects or samples.
          </Text>
        )
      )}
    </div>
  );
}
